import { Alert } from '@creditornot/cb-alert';
import { Button, TextButtonLink } from '@creditornot/cb-button';
import { InputContainer } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { useHCaptcha } from 'common/hooks';
import { HCaptchaErrorStatus, isHCaptchaErrorStatus } from 'common/hooks/useHCaptcha';

import FacebookButton from './FacebookButton';
import { useLoginContext } from '../hooks';

type Props = {
  email: string;
  isEmployee: boolean;
  onEmailChange: (email: string) => void;
  onEmailLoginSuccess: () => void;
};

const emailSchema = yup.string().required().email();

const getSignUpUrl = () => {
  return `${process.env.WOLTCOM_URI}/signUp`;
};

const NextButton = styled(Button).attrs({
  variant: 'blue',
  size: 'large',
  stretch: true,
})`
  margin-block-start: 24px;
`;

const ErrorAlert = styled(Alert).attrs({
  variant: 'error',
})`
  margin-block-start: 16px;
`;

const SignUpLink = styled(TextButtonLink)`
  margin: 0 auto;
`;

const Footer = styled.footer`
  padding: 1rem 0;
  text-align: center;
`;

const FacebookButtonContainer = styled(FacebookButton)`
  background-color: ${({ theme }) => theme.cbSemanticColors.surfaceMain};
  border: 1px solid ${({ theme }) => theme.cbSemanticColors.borderPrimary};
  color: ${({ theme }) => theme.cbSemanticColors.textSecondary};
`;

const CreateAccountLinkContainer = styled.span`
  color: ${({ theme }) => theme.cbSemanticColors.textPrimary};
`;

const getHCaptchaErrorMessage = (status: HCaptchaErrorStatus): JSX.Element => {
  switch (status) {
    case 'error':
      return <FormattedMessage id="landingPage.reCaptchaError" />;
    case 'expired':
      return <FormattedMessage id="landingPage.reCaptchaExpired" />;
    case 'timeout':
      return <FormattedMessage id="landingPage.hCaptchaTimedOut" />;
  }
};

const LoginBox = ({ email, isEmployee, onEmailChange, onEmailLoginSuccess }: Props) => {
  const intl = useIntl();
  const { sitekey, captchaRef, hCaptchaStatus, runWithHCaptcha, hCaptchaHandlers } = useHCaptcha('wolt-auth');

  const {
    didFailEmailLogin,
    didFailFacebookLogin,
    didFailGoogleLogin,
    isLoadingEmailLogin,
    isLoadingFacebookLogin,
    onEmailLogin,
    isLoadingGoogleLogin,
    onFacebookLogin,
    onGoogleLogin,
  } = useLoginContext();

  const isValidEmail = useMemo(() => emailSchema.isValidSync(email), [email]);

  const next = async () => {
    if (isEmployee) {
      onGoogleLogin();
      return;
    }

    await runWithHCaptcha(async hCaptchaResponse => {
      await onEmailLogin({ email, captchaHeader: { 'h-captcha-response': hCaptchaResponse } });
      onEmailLoginSuccess();
    });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    next();
  };

  const loading = isLoadingEmailLogin || isLoadingGoogleLogin || hCaptchaStatus === 'busy';

  return (
    <>
      <form onSubmit={handleSubmit}>
        <InputContainer
          label={intl.formatMessage({
            id: 'login.email',
          })}
          labelHtmlFor="email"
          style={{
            marginBlockStart: 24,
          }}
        >
          <Input
            type="email"
            id="email"
            placeholder={intl.formatMessage({
              id: 'login.placeholder.email',
            })}
            value={email}
            onChange={event => {
              onEmailChange(event.currentTarget.value);
            }}
            disabled={loading}
            autoFocus
          />
        </InputContainer>

        <NextButton type="submit" disabled={!isValidEmail} loading={loading}>
          <FormattedMessage id="login.next" />
        </NextButton>

        <HCaptcha size="invisible" sitekey={sitekey} ref={captchaRef} {...hCaptchaHandlers} />

        {isHCaptchaErrorStatus(hCaptchaStatus) && <ErrorAlert>{getHCaptchaErrorMessage(hCaptchaStatus)}</ErrorAlert>}
      </form>

      {didFailGoogleLogin && (
        <ErrorAlert>
          <FormattedMessage id="login.googleError" />
        </ErrorAlert>
      )}

      {didFailEmailLogin && (
        <ErrorAlert>
          <FormattedMessage id="login.emailLoginError" />
        </ErrorAlert>
      )}

      <Footer>
        <FacebookButtonContainer
          loading={isLoadingFacebookLogin}
          onClick={onFacebookLogin}
          disabled={isLoadingFacebookLogin || isEmployee}
          iconColor="#1877F2"
        >
          <FormattedMessage id="login.continueWithFacebook" />
        </FacebookButtonContainer>
        {didFailFacebookLogin && (
          <ErrorAlert>
            <FormattedMessage id="login.facebookError" />
          </ErrorAlert>
        )}
      </Footer>
      <Footer>
        <CreateAccountLinkContainer>
          <FormattedMessage id="landingPage.createAccountLink" />{' '}
        </CreateAccountLinkContainer>
        <SignUpLink href={getSignUpUrl()}>
          <FormattedMessage id="landingPage.getOneHere" />
        </SignUpLink>
      </Footer>
    </>
  );
};

export default LoginBox;
